// BTC-first amount entry for the trade doors (phone sheet, desktop box).
//
// The unit ladder the user types in:
//
//  - BUYS enter in BTC. The typed figure IS the sell-side amount — sats-exact,
//    no float hop on the money path. The ⇅ still offers USD (converted at the
//    live BTC price) and token-unit entry (exact-out), but BTC is the default.
//  - SELLS enter in the token being sold. You sell what you hold, and the 100%
//    chip must sell the exact held amount — a BTC-denominated sell would
//    round-trip through two prices and strand dust. The ⇅ offers BTC and USD.
//
// Everything here DECIDES a decimal string; the audited panel still owns the
// quote, the guards, the frozen review and the signature.

export type EntryUnit = 'btc' | 'usd' | 'token'
export type Side = 'buy' | 'sell'

export interface EntryCtx {
	/// Live BTC price in USD, when known.
	btcUsd: number | null
	/// The asset's own USD price, when known.
	price: number | null
}

/// Append one numpad key to an amount string, refusing anything that stops
/// being a number: a second dot, more decimals than the unit can carry, or a
/// figure longer than the display can hold.
export function press(raw: string, key: string, maxDecimals: number): string {
	if (key === '⌫') {
		return raw.slice(0, -1)
	}
	if (key === '.') {
		if (raw.includes('.')) {
			return raw
		}
		return raw === '' ? '0.' : `${raw}.`
	}
	const next = raw === '0' && key !== '0' ? key : raw === '0' && key === '0' ? raw : raw + key
	const dot = next.indexOf('.')
	if (dot !== -1 && next.length - dot - 1 > maxDecimals) {
		return raw
	}
	if ((dot === -1 ? next.length : dot) > 9) {
		return raw
	}
	return next
}

/// Trim a float to a `parseAmount`-friendly decimal string with no float tail.
/// Every door computes prefills through this or they are not the same door.
export function toAmountString(value: number, decimals: number): string {
	if (!Number.isFinite(value) || value <= 0) {
		return ''
	}
	return value.toFixed(decimals).replace(/0+$/, '').replace(/\.$/, '')
}

/// Thousands separators for the whole part of an in-progress entry, leaving
/// the fraction (and a trailing dot) untouched.
export function groupWhole(raw: string): string {
	const dot = raw.indexOf('.')
	const whole = dot === -1 ? raw : raw.slice(0, dot)
	if (whole.length < 4 || !/^\d+$/.test(whole)) {
		return raw
	}
	return `${BigInt(whole).toLocaleString('en-US')}${dot === -1 ? '' : raw.slice(dot)}`
}

/// How many decimals a unit can honestly carry: sats for BTC, cents for USD,
/// the tokens' own 8dp otherwise.
export function unitDecimals(unit: EntryUnit): number {
	return unit === 'usd' ? 2 : 8
}

/// The ⇅ cycle for a side, restricted to units the live data can convert.
/// BTC entry needs nothing on a buy (it IS the input); on a sell it needs both
/// prices to reach a token amount. Token entry needs nothing on a buy
/// (exact-out) and nothing on a sell (it IS the input).
///
/// USD left the ladder 2026-09-02: you TYPE in BTC or the token, and the
/// dollar figure rides along as the display conversion — entering a trade in
/// a unit the chain never settles in was one flip too many. `EntryUnit` keeps
/// 'usd' because `convertEntry(..., 'usd', ...)` still powers that display.
export function entryUnits(side: Side, ctx: EntryCtx): EntryUnit[] {
	if (side === 'buy') {
		return ['btc', 'token']
	}
	const units: EntryUnit[] = ['token']
	const priced = ctx.price !== null && ctx.price > 0
	if (priced && ctx.btcUsd !== null && ctx.btcUsd > 0) {
		units.push('btc')
	}
	return units
}

/// The unit after one ⇅ press. A unit that has become unavailable (price feed
/// dropped mid-session) resolves to the side's default.
export function nextUnit(unit: EntryUnit, side: Side, ctx: EntryCtx): EntryUnit {
	const units = entryUnits(side, ctx)
	const at = units.indexOf(unit)
	return units[(at + 1) % units.length] ?? units[0]
}

/// A raw entry as a number; '' and '.' are zero, never NaN.
export function typedNumber(raw: string): number {
	if (raw === '' || raw === '.') {
		return 0
	}
	const n = Number.parseFloat(raw)
	return Number.isFinite(n) ? n : 0
}

/// The same quantity re-expressed for the unit not being typed — the
/// converter line and the value carried across a ⇅ flip. USD is the bridge
/// between BTC and token terms. Null when the conversion has no live rate.
export function convertEntry(
	value: number,
	from: EntryUnit,
	to: EntryUnit,
	ctx: EntryCtx
): number | null {
	if (from === to) {
		return value
	}
	if (value <= 0 || !Number.isFinite(value)) {
		return 0
	}
	const { btcUsd, price } = ctx
	const usd =
		from === 'usd'
			? value
			: from === 'btc'
			? btcUsd !== null && btcUsd > 0
				? value * btcUsd
				: null
			: price !== null && price > 0
			? value * price
			: null
	if (usd === null) {
		return null
	}
	if (to === 'usd') {
		return usd
	}
	if (to === 'btc') {
		return btcUsd !== null && btcUsd > 0 ? usd / btcUsd : null
	}
	return price !== null && price > 0 ? usd / price : null
}

/// Carry the on-screen quantity across a flip so the number stays the same
/// value in the other unit. An impossible conversion clears the entry rather
/// than inventing one.
export function carryAcrossFlip(
	raw: string,
	from: EntryUnit,
	to: EntryUnit,
	ctx: EntryCtx
): string {
	const typed = typedNumber(raw)
	if (typed <= 0) {
		return ''
	}
	const converted = convertEntry(typed, from, to, ctx)
	if (converted === null || converted <= 0) {
		return ''
	}
	return toAmountString(converted, unitDecimals(to))
}

/// What the entry hands the audited panel. Exactly one of the fields:
///  - buy in BTC   → sell-side BTC amount, the typed string passed through
///    (sats-exact — no float round trip)
///  - buy in USD   → sell-side BTC amount at the live price
///  - buy in token → exact-out buy-side amount
///  - sell (any)   → sell-side token amount
/// Null when the amount is zero or a needed rate is missing.
export function prefillFor(
	side: Side,
	unit: EntryUnit,
	raw: string,
	ctx: EntryCtx
): { sell?: string; buy?: string } | null {
	const typed = typedNumber(raw)
	if (typed <= 0) {
		return null
	}
	if (side === 'buy') {
		if (unit === 'btc') {
			const s = toAmountString(typed, 8)
			return s ? { sell: s } : null
		}
		if (unit === 'usd') {
			const btc = convertEntry(typed, 'usd', 'btc', ctx)
			const s = btc !== null ? toAmountString(btc, 8) : ''
			return s ? { sell: s } : null
		}
		const t = toAmountString(typed, 8)
		return t ? { buy: t } : null
	}
	const tokenAmt = unit === 'token' ? typed : convertEntry(typed, unit, 'token', ctx)
	if (tokenAmt === null || tokenAmt <= 0) {
		return null
	}
	const s = toAmountString(tokenAmt, 8)
	return s ? { sell: s } : null
}

/// The BTC-side input a buy needs for the funding decision, when the unit can
/// say without a quote. Token-unit entry needs the exact-out quote inverted —
/// the caller owns that (it already runs the same inversion the panel will).
export function btcSideSats(unit: EntryUnit, raw: string, ctx: EntryCtx): bigint | null {
	const typed = typedNumber(raw)
	if (typed <= 0) {
		return null
	}
	if (unit === 'btc') {
		return BigInt(Math.round(typed * 1e8))
	}
	if (unit === 'usd') {
		const btc = convertEntry(typed, 'usd', 'btc', ctx)
		return btc !== null ? BigInt(Math.round(btc * 1e8)) : null
	}
	return null
}

/// Raw base units as a plain (ungrouped) decimal string, full 8dp precision
/// with trailing zeros trimmed — the exact-entry form of a chip fill.
export function plainAmount(raw: bigint): string {
	const neg = raw < 0n
	const abs = neg ? -raw : raw
	const whole = abs / 100_000_000n
	const frac = (abs % 100_000_000n).toString().padStart(8, '0').replace(/0+$/, '')
	return `${neg ? '-' : ''}${whole.toString()}${frac ? `.${frac}` : ''}`
}

/// A %-of-balance chip, computed in bigint so 100% is the exact balance.
/// `spendableRaw` is sats on a buy (the funding ladder's spendable) and the
/// held token's base units on a sell. The result is re-expressed in the unit
/// being typed; BTC-on-buy and token-on-sell pass through exactly.
export function pctEntry(
	side: Side,
	unit: EntryUnit,
	spendableRaw: bigint,
	pct: number,
	ctx: EntryCtx
): string {
	if (spendableRaw <= 0n || pct <= 0) {
		return ''
	}
	const raw = (spendableRaw * BigInt(Math.round(pct))) / 100n
	if (raw <= 0n) {
		return ''
	}
	const exact = plainAmount(raw)
	const nativeUnit: EntryUnit = side === 'buy' ? 'btc' : 'token'
	if (unit === nativeUnit) {
		return exact
	}
	const converted = convertEntry(Number(raw) / 1e8, nativeUnit, unit, ctx)
	if (converted === null || converted <= 0) {
		return ''
	}
	return toAmountString(converted, unitDecimals(unit))
}
